import React, { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import { createSshGrant, listSshGrants } from "../api/sshAccessAdminApi";
import { getEdges } from "../api/edgeApi";
import { formatDate } from "../utils/i18nFormat";

const tableStyle = {
  width: "100%",
  borderCollapse: "collapse",
  fontSize: 14,
};
const thStyle = {
  textAlign: "left",
  padding: "10px 12px",
  borderBottom: "1px solid var(--border)",
  color: "var(--text-muted)",
  fontWeight: 600,
};
const tdStyle = {
  padding: "10px 12px",
  borderBottom: "1px solid var(--border)",
  verticalAlign: "middle",
};
const cardStyle = {
  display: "grid",
  gap: 14,
  maxWidth: 960,
  padding: 20,
  border: "1px solid var(--border)",
};
const fieldStyle = { display: "grid", gap: 6 };

/** Superadmin on-demand reverse-SSH remote support: Valonix's own internal
 * tool, separate from the tenant-facing SoftEther connect-edge bridge. */
export default function SshAccess() {
  const { t } = useTranslation();
  const accessToken = useSelector((s) => s.session?.accessToken);
  const [edges, setEdges] = useState([]);
  const [grants, setGrants] = useState([]);
  const [edgeDbId, setEdgeDbId] = useState("");
  const [ttlMinutes, setTtlMinutes] = useState(30);
  const [busy, setBusy] = useState(false);
  const [lastGrant, setLastGrant] = useState(null);
  const PAGE_SIZE = 25;
  const [page, setPage] = useState(0);
  const [totalGrants, setTotalGrants] = useState(0);
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");

  useEffect(() => {
    const timer = setTimeout(() => {
      setPage(0);
      setSearch(searchInput.trim());
    }, 300);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const refresh = useCallback(async () => {
    if (!accessToken) return;
    const [edgesRes, grantsRes] = await Promise.all([
      getEdges(accessToken),
      listSshGrants(accessToken, { limit: PAGE_SIZE, offset: page * PAGE_SIZE, q: search || undefined }),
    ]);
    setEdges(Array.isArray(edgesRes) ? edgesRes : edgesRes?.items || []);
    setGrants(grantsRes.items);
    setTotalGrants(grantsRes.total);
  }, [accessToken, page, search]);

  useEffect(() => {
    refresh().catch(() => toast.error(t("vpn.ssh.loadFailed")));
  }, [refresh, t]);

  const requestGrant = async (event) => {
    event.preventDefault();
    if (!edgeDbId) return;
    setBusy(true);
    try {
      const res = await createSshGrant(accessToken, {
        edge_db_id: edgeDbId,
        ttl_minutes: Number(ttlMinutes),
      });
      setLastGrant(res.data);
      setPage(0);
      await refresh();
      toast.success(t("vpn.ssh.grantCreated"));
    } catch (error) {
      toast.error(error.response?.data?.detail || t("vpn.ssh.grantFailed"));
    } finally {
      setBusy(false);
    }
  };

  const downloadKey = () => {
    const blob = new Blob([lastGrant.device_access_private_key_pem], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `ssh-access-grant-${lastGrant.id}.pem`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // relay_host/port/id come from the server and land in a copy-paste shell command:
  // refuse anything that is not a plain hostname/IP, port number or id token.
  const SAFE_HOST = /^[A-Za-z0-9]([A-Za-z0-9.-]*[A-Za-z0-9])?$/;
  const connectCommands = (grant) =>
    !SAFE_HOST.test(String(grant.relay_host)) || !/^\d{1,5}$/.test(String(grant.relay_port)) || !/^[\w-]+$/.test(String(grant.id))
      ? t("vpn.ssh.invalidRelay", { defaultValue: "Relay details from the server look invalid; not generating commands." })
      : [
      `chmod 600 ~/Downloads/ssh-access-grant-${grant.id}.pem`,
      `ssh -f -N -L ${grant.relay_port}:localhost:${grant.relay_port} root@${grant.relay_host}`,
      `ssh -p ${grant.relay_port} -i ~/Downloads/ssh-access-grant-${grant.id}.pem root@localhost`,
        ].join("\n");

  return (
    <div style={{ display: "grid", gap: 28 }}>
      <header>
        <h1>{t("vpn.ssh.title")}</h1>
        <p>{t("vpn.ssh.intro", { host: grants[0]?.relay_host || "94.138.209.88" })}</p>
      </header>

      <form onSubmit={requestGrant} style={cardStyle}>
        <h2>{t("vpn.ssh.newGrant")}</h2>
        <label style={fieldStyle}>
          {t("vpn.ssh.device")}
          <select
            required
            value={edgeDbId}
            onChange={(e) => setEdgeDbId(e.target.value)}
          >
            <option value="">{t("vpn.ssh.select")}</option>
            {edges.map((e) => (
              <option key={e.id} value={e.id}>
                {e.name || e.edgeId || e.edge_id || e.id}
              </option>
            ))}
          </select>
        </label>
        <label style={fieldStyle}>
          {t("vpn.ssh.ttl")}
          <input
            type="number"
            min="5"
            max="240"
            value={ttlMinutes}
            onChange={(e) => setTtlMinutes(e.target.value)}
          />
        </label>
        <button disabled={busy} type="submit">
          {t("vpn.ssh.openTunnel")}
        </button>
      </form>

      {lastGrant && (
        <section style={cardStyle}>
          <h2>{t("vpn.ssh.ready", { id: lastGrant.id })}</h2>
          <p style={{ margin: 0, color: "var(--text-muted)" }}>
            {t("vpn.ssh.keyNotice")}
          </p>
          <button type="button" onClick={downloadKey}>
            {t("vpn.ssh.downloadKey")}
          </button>
          <label style={fieldStyle}>
            {t("vpn.ssh.commands")}
            <textarea readOnly rows={3} value={connectCommands(lastGrant)} />
          </label>
        </section>
      )}

      <section style={cardStyle}>
        <h2>{t("vpn.ssh.history")}</h2>
        <input
          placeholder={t("vpn.ssh.searchPlaceholder")}
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
        />
        <table style={tableStyle}>
          <thead>
            <tr>
              <th style={thStyle}>{t("vpn.ssh.col.device")}</th>
              <th style={thStyle}>{t("vpn.ssh.col.relay")}</th>
              <th style={thStyle}>{t("vpn.ssh.col.status")}</th>
              <th style={thStyle}>{t("vpn.ssh.col.created")}</th>
              <th style={thStyle}>{t("vpn.ssh.col.expires")}</th>
            </tr>
          </thead>
          <tbody>
            {grants.map((item) => {
              const edge = edges.find((e) => e.id === item.edge_db_id);
              return (
                <tr key={item.id}>
                  <td style={tdStyle}>{edge?.name || item.edge_db_id}</td>
                  <td style={tdStyle}>
                    <code>{item.relay_host}:{item.relay_port}</code>
                  </td>
                  <td style={tdStyle}>
                    <strong>{t(`vpn.ssh.status.${item.status}`, { defaultValue: item.status })}</strong>
                    {item.error && (
                      <div style={{ color: "var(--text-muted)", fontSize: 12 }}>{item.error}</div>
                    )}
                  </td>
                  <td style={tdStyle}>
                    {formatDate(item.created_at)}
                  </td>
                  <td style={tdStyle}>
                    {formatDate(item.expires_at)}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
          <button type="button" disabled={page === 0} onClick={() => setPage((p) => p - 1)}>
            {t("common.previous")}
          </button>
          <span style={{ color: "var(--text-muted)", fontSize: 13 }}>
            {totalGrants === 0 ? "0" : `${page * PAGE_SIZE + 1}-${Math.min((page + 1) * PAGE_SIZE, totalGrants)}`} / {totalGrants}
          </span>
          <button
            type="button"
            disabled={(page + 1) * PAGE_SIZE >= totalGrants}
            onClick={() => setPage((p) => p + 1)}
          >
            {t("common.next")}
          </button>
        </div>
      </section>
    </div>
  );
}
